import React from 'react';

import { Button } from 'is24-corecss';
import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import { FAQTab } from '../../../../common/display/faqTab/FAQTab';
import { OverviewTab } from './components/Overview';
import { useProductStatus } from '../../../../../customHooks/useProductStatus.hook';
import { ProductsStatusKey } from '../../../../../@types/productsStatus';
import { TurtleboxState } from '../../../../../@types/turtleBoxState';
import { useGetHasEntitlement } from '../../../../../customHooks/useGetHasEntitlement.hook';
import { EntitlementType } from '../../../../../@types/entitlement';
import { HowItWorksTab } from './components/HowItWorks';
import { report } from '../../../../../tracking/tracking';
import { TrackingEvents } from '../../../../../tracking/trackingEvents';

import TurtleBoxPng from './images/turtlebox.png';
import TurtleBoxWebp from './images/turtlebox.webp';
import TurtleBoxPngMobile from './images/turtlebox_mobile.png';
import TurtleBoxWebpMobile from './images/turtlebox_mobile.webp';
import { Msg, useIntl } from '@/i18n';

const ButtonArea = ({
  isEntitled,
  hasDiscountCode,
  onClick,
}: {
  isEntitled: boolean;
  hasDiscountCode: boolean;
  onClick: () => void;
}) => {
  const ctaMessage = isEntitled
    ? 'benefits.relocationBox.modal.buttonCta.redeemDiscout'
    : 'benefits.relocationBox.modal.buttonCta.continueWithDiscount';
  return (
    <div className='align-center'>
      <Button appearance='secondary' disabled={!hasDiscountCode} onClick={onClick}>
        <Msg id={ctaMessage} defaultMessage='To relocation box' />
      </Button>
    </div>
  );
};

export const RelocationBox = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const productStatus = useProductStatus(ProductsStatusKey.Turtlebox) as TurtleboxState;
  const hasRelocationBoxEntitlement = useGetHasEntitlement(EntitlementType.RELOCATION_BOX);

  const onCtaClick = React.useCallback(async () => {
    // TODO: Check with Diego about the value of the second parameter passed to report
    await report(TrackingEvents.RELOCATION_BOX.LINK_CLICKED, hasRelocationBoxEntitlement);
    window.open(productStatus.url, '_self');
  }, [report, productStatus, hasRelocationBoxEntitlement]);

  return (
    <Modal
      title={t({ id: 'benefits.relocationBox.headline', defaultMessage: 'Relocation box' })}
      isVisible={isVisible}
      image={TurtleBoxPng}
      imageWebp={TurtleBoxWebp}
      imageMobile={TurtleBoxPngMobile}
      imageMobileWebp={TurtleBoxWebpMobile}
      imageUseFullWidth={false}
      imageAlt={t({
        id: 'benefits.relocationBox.modal.image',
        defaultMessage: 'Items packed in boxes',
      })}
      tabsContent={[
        <OverviewTab
          isEntitled={hasRelocationBoxEntitlement}
          discountCode={productStatus?.discountCode}
          key='benefits.relocationBox.modal.overview'
        />,
        <HowItWorksTab key='benefits.relocationBox.modal.howItWorks' />,
        <FAQTab includeCustomerService={false} key='benefits.relocationBox.modal.faq' benefit='relocationBox' />,
      ]}
      buttonArea={
        <ButtonArea
          onClick={onCtaClick}
          isEntitled={hasRelocationBoxEntitlement}
          hasDiscountCode={!!productStatus?.discountCode}
        />
      }
      benefit={Benefit.OPPORTUNITY_CHECK}
      onClose={onClose}
    />
  );
};
